import { Shell } from "@/components/layout/Shell";
import { useEmpresas, useResumoCreditosParceiros, type ResumoCreditosParceiro } from "@/hooks/use-data";
import { useAuth } from "@/hooks/use-auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Building2,
  MapPin,
  Mail,
  Phone,
  Plus,
  X,
  Loader2,
  DollarSign,
  Upload,
  Users,
  CalendarFold,
  Search,
} from "lucide-react";
import { useState, useMemo, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { LogoAvatar } from "@/components/LogoUpload";
import { api } from "@shared/routes";
import { ImportarParceirosDialog } from "@/components/empresas/ImportarParceirosDialog";
import { useModulos } from "@/hooks/use-modulos";
import {
  fmtDateTime,
  formatCnpj,
  formatCpf,
  formatPhone,
} from "@/lib/formatters";
import { UF_LIST } from "@/lib/constants";
import { PreencherTesteButton } from "@/components/PreencherTesteButton";
import { gerarArtFake, gerarCnpjFake } from "@/lib/mock-test-data";
import { useIsEmpresaTestMode } from "@/hooks/use-modo-teste";
import { TagParceiroField, useTagParceiroDisponibilidade } from "@/components/empresas/TagParceiroField";
import { normalizarTagParceiro, tagParceiroValida } from "@/lib/tag-parceiro";
import type { Empresa } from "@shared/schema";

type EmpresaComTagParceiro = Empresa & {
  tagParceiro?: string | null;
  podeEditarTagParceiro?: boolean;
};

function CreateEmpresaForm({
  tipo,
  vistoriadoras: vistoriadoras,
  onClose,
  isOwnerCreating = false,
  artMultipla = false,
  artPorRt = false,
}: {
  tipo: "VISTORIADORA" | "PARCEIRO_DE_NEGOCIOS";
  vistoriadoras: { id: string; razaoSocial: string }[];
  onClose: () => void;
  isOwnerCreating?: boolean;
  artMultipla?: boolean;
  artPorRt?: boolean;
}) {
  const { session, profile } = useAuth();
  const { toast } = useToast();
  const [razaoSocial, setRazaoSocial] = useState("");
  const [nomeFantasia, setNomeFantasia] = useState("");
  const [cnpj, setCnpj] = useState("");
  const [cpf, setCpf] = useState("");
  const [uf, setUf] = useState("");
  const [email, setEmail] = useState("");
  const [telefone, setTelefone] = useState("");
  const [vistoriadoraId, setVistoriadoraId] = useState("");
  const vistoriadoraModoTeste = useIsEmpresaTestMode(
    isOwnerCreating ? vistoriadoraId : profile?.empresaId,
  );
  const [cidade, setCidade] = useState("");
  const [tagEmpresa, setTagEmpresa] = useState("");
  const [tagParceiro, setTagParceiro] = useState("");
  const [idProprio, setIdProprio] = useState("");
  const [art, setArt] = useState("");
  const [vencimentoArt, setVencimentoArt] = useState("");
  const disponibilidadeTag = useTagParceiroDisponibilidade({
    value: tagParceiro,
    token: session?.access_token,
  });

  const mutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await fetch("/api/empresas", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Erro ao criar empresa");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.empresas.list.path] });
      toast({
        title: "Empresa criada",
        description: `${tipo === "VISTORIADORA" ? "Vistoriadora" : "Parceiro de Negócios"} cadastrado(a) com sucesso.`,
      });
      onClose();
    },
    onError: (err: Error) => {
      toast({
        title: "Erro",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const preencherTeste = () => {
    setRazaoSocial((v) => v || "Empresa Teste Ltda");
    setNomeFantasia((v) => v || "Teste Fantasia");
    setCnpj((v) => v || formatCnpj(gerarCnpjFake()));
    setUf((v) => v || "SP");
    setEmail((v) => v || `teste.${Date.now()}@teste.com`);
    setTelefone((v) => v || formatPhone("11999999999"));
    setCidade((v) => v || "São Paulo");
    if (tipo === "VISTORIADORA") {
      setTagEmpresa((v) => v || "TESTE");
    }
    if (tipo === "PARCEIRO_DE_NEGOCIOS" && !artMultipla && !artPorRt) {
      setArt((v) => v || gerarArtFake());
      setVencimentoArt((v) => v || "2099-12-31");
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!razaoSocial || !uf || !email || !cidade) return;

    // Para vistoriadora, tagEmpresa é obrigatória
    if (tipo === "VISTORIADORA" && !tagEmpresa) {
      toast({
        title: "Erro",
        description: "Tag é obrigatória para vistoriadora",
        variant: "destructive",
      });
      return;
    }

    if (tipo === "PARCEIRO_DE_NEGOCIOS") {
      if (!tagParceiroValida(tagParceiro)) {
        toast({ title: "TAG inválida", description: "Use 1 a 10 letras ou números; comece por uma letra.", variant: "destructive" });
        return;
      }
      if (session?.access_token && disponibilidadeTag.status !== "disponivel") {
        const description = disponibilidadeTag.status === "ocupada"
          ? "Essa TAG já está sendo usada. Escolha outra."
          : disponibilidadeTag.status === "erro"
            ? "Não foi possível verificar a TAG. Tente novamente."
            : "Aguarde a verificação de disponibilidade da TAG.";
        toast({ title: "Verifique a TAG", description, variant: "destructive" });
        return;
      }
    }

    // Para parceiro de negócios sem ART Múltipla e sem ART por RT, ART e vencimento são obrigatórios
    if (
      tipo === "PARCEIRO_DE_NEGOCIOS" &&
      !artMultipla &&
      !artPorRt &&
      (!art || !vencimentoArt)
    ) {
      toast({
        title: "Erro",
        description:
          "ART e Vencimento do ART são obrigatórios para parceiro de negócios",
        variant: "destructive",
      });
      return;
    }

    const data: any = {
      tipo,
      razaoSocial,
      nomeFantasia: nomeFantasia || undefined,
      cnpj: cnpj.replace(/\D/g, "") || undefined,
      cpf: cpf.replace(/\D/g, "") || undefined,
      uf,
      email,
      telefone: telefone || undefined,
      cidade,
    };

    if (tipo === "VISTORIADORA" && tagEmpresa) {
      data.tagEmpresa = tagEmpresa.toUpperCase();
    }

    if (tipo === "PARCEIRO_DE_NEGOCIOS") {
      data.tagParceiro = normalizarTagParceiro(tagParceiro);
    }

    if (tipo === "PARCEIRO_DE_NEGOCIOS" && vistoriadoraId) {
      data.vistoriadoraId = vistoriadoraId;
    }

    if (tipo === "PARCEIRO_DE_NEGOCIOS" && idProprio) {
      const parsed = parseInt(idProprio, 10);
      if (!isNaN(parsed)) data.idProprio = parsed;
    }

    if (tipo === "PARCEIRO_DE_NEGOCIOS" && !artMultipla && !artPorRt) {
      data.art = art;
      data.vencimentoArt = vencimentoArt;
    }

    mutation.mutate(data);
  };

  return (
    <Card className="border-2 border-primary/20 shadow-lg">
      <CardHeader className="flex flex-row items-center justify-between pb-4">
        <CardTitle className="text-base">
          Nova{" "}
          {tipo === "VISTORIADORA" ? "Vistoriadora" : "Parceiro de Negócios"}
        </CardTitle>
        <div className="flex items-center gap-2">
          {vistoriadoraModoTeste && (
            <PreencherTesteButton onFill={preencherTeste} />
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            data-testid="button-close-form"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Razão Social *</Label>
              <Input
                value={razaoSocial}
                onChange={(e) => setRazaoSocial(e.target.value)}
                required
                data-testid="input-empresa-razao"
              />
            </div>
            <div className="space-y-2">
              <Label>Nome Fantasia</Label>
              <Input
                value={nomeFantasia}
                onChange={(e) => setNomeFantasia(e.target.value)}
                data-testid="input-empresa-fantasia"
              />
            </div>
            <div className="space-y-2">
              <Label>CNPJ</Label>
              <Input
                value={cnpj}
                onChange={(e) => setCnpj(formatCnpj(e.target.value))}
                placeholder="00.000.000/0000-00"
                data-testid="input-empresa-cnpj"
              />
            </div>
            <div className="space-y-2">
              <Label>CPF</Label>
              <Input
                value={cpf}
                onChange={(e) => setCpf(formatCpf(e.target.value))}
                placeholder="000.000.000-00"
                data-testid="input-empresa-cpf"
              />
            </div>
            <div className="space-y-2">
              <Label>E-mail *</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="input-empresa-email"
              />
            </div>
            <div className="space-y-2">
              <Label>UF *</Label>
              <Select value={uf} onValueChange={setUf}>
                <SelectTrigger data-testid="select-empresa-uf">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {UF_LIST.map((u) => (
                    <SelectItem key={u} value={u}>
                      {u}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Telefone</Label>
              <Input
                value={telefone}
                onChange={(e) => setTelefone(formatPhone(e.target.value))}
                placeholder="(00) 00000-0000"
                maxLength={15}
                inputMode="numeric"
                data-testid="input-empresa-telefone"
              />
            </div>
            <div className="space-y-2">
              <Label>Cidade *</Label>
              <Input
                value={cidade}
                onChange={(e) => setCidade(e.target.value)}
                required
                data-testid="input-empresa-cidade"
              />
            </div>
            {tipo === "PARCEIRO_DE_NEGOCIOS" && (
              <div className="space-y-2">
                <Label>ID Próprio</Label>
                <Input
                  type="number"
                  value={idProprio}
                  onChange={(e) => setIdProprio(e.target.value)}
                  placeholder="Código numérico interno (opcional)"
                  data-testid="input-empresa-id-proprio"
                />
                <p className="text-xs text-muted-foreground">
                  Identificador numérico próprio da vistoriadora para este
                  parceiro. Facilita a identificação quando há nomes similares.
                </p>
              </div>
            )}
            {tipo === "PARCEIRO_DE_NEGOCIOS" && (
              <TagParceiroField
                value={tagParceiro}
                onChange={setTagParceiro}
                availability={disponibilidadeTag}
                required
                testId="input-empresa-tag-parceiro"
              />
            )}
            {tipo === "PARCEIRO_DE_NEGOCIOS" && !artMultipla && !artPorRt && (
              <div className="space-y-1.5">
                <Label>ART *</Label>
                <Input
                  value={art}
                  onChange={(e) => setArt(e.target.value)}
                  placeholder="Número da ART"
                  required
                  data-testid="input-empresa-art"
                />
              </div>
            )}
            {tipo === "PARCEIRO_DE_NEGOCIOS" && !artMultipla && !artPorRt && (
              <div className="space-y-2">
                <Label>Vencimento do ART *</Label>
                <Input
                  type="date"
                  value={vencimentoArt}
                  onChange={(e) => setVencimentoArt(e.target.value)}
                  required
                  data-testid="input-empresa-vencimento-art"
                />
              </div>
            )}
            {tipo === "VISTORIADORA" && (
              <div className="space-y-2 md:col-span-2">
                <div>
                  <Label>Tag para Identificação de Laudos *</Label>
                  <p className="text-xs text-muted-foreground mt-1 mb-2">
                    Código único usado para identificar os laudos. Ex: Se sua
                    tag é <strong>PFLOW</strong>, os laudos serão: PFLOW-001,
                    PFLOW-002, etc.
                  </p>
                </div>
                <Input
                  value={tagEmpresa}
                  onChange={(e) => setTagEmpresa(e.target.value.toUpperCase())}
                  placeholder="Ex: PFLOW"
                  maxLength={10}
                  data-testid="input-empresa-tag"
                  required
                />
              </div>
            )}
            {tipo === "PARCEIRO_DE_NEGOCIOS" &&
              isOwnerCreating &&
              vistoriadoras.length > 0 && (
                <div className="space-y-2 md:col-span-2">
                  <Label>Vinculada à Vistoriadora *</Label>
                  <Select
                    value={vistoriadoraId}
                    onValueChange={setVistoriadoraId}
                  >
                    <SelectTrigger data-testid="select-empresa-vistoriadora">
                      <SelectValue placeholder="Selecione a vistoriadora" />
                    </SelectTrigger>
                    <SelectContent>
                      {vistoriadoras.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.razaoSocial}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={mutation.isPending}
              data-testid="button-submit-empresa"
            >
              {mutation.isPending && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              Cadastrar
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function TagParceiroResumo({ empresa, token }: { empresa: EmpresaComTagParceiro; token?: string }) {
  const { toast } = useToast();
  const [editando, setEditando] = useState(false);
  const [tag, setTag] = useState(empresa.tagParceiro ?? "");
  const podeEditar = empresa.podeEditarTagParceiro === true;
  const disponibilidade = useTagParceiroDisponibilidade({
    value: tag,
    token,
    empresaId: empresa.id,
    originalValue: empresa.tagParceiro,
  });

  useEffect(() => setTag(empresa.tagParceiro ?? ""), [empresa.id, empresa.tagParceiro]);

  const mutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`/api/empresas/${empresa.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ tagParceiro: normalizarTagParceiro(tag) }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.message || "Não foi possível salvar a TAG.");
      return body;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [api.empresas.list.path] }),
        queryClient.invalidateQueries({ queryKey: [api.empresas.get.path, empresa.id] }),
      ]);
      setEditando(false);
      toast({ title: "TAG atualizada", description: "A TAG do Parceiro foi salva." });
    },
    onError: (error: Error) => toast({ title: "Erro ao salvar TAG", description: error.message, variant: "destructive" }),
  });

  return (
    <div className="rounded-md border bg-muted/20 px-3 py-2" data-testid={`tag-parceiro-${empresa.id}`}>
      {editando ? (
        <div className="space-y-2">
          <TagParceiroField
            value={tag}
            onChange={setTag}
            availability={disponibilidade}
            required
            testId={`input-tag-parceiro-${empresa.id}`}
          />
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" size="sm" variant="outline" onClick={() => { setTag(empresa.tagParceiro ?? ""); setEditando(false); }} disabled={mutation.isPending}>Cancelar</Button>
            <Button
              type="button"
              size="sm"
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending || !tagParceiroValida(tag) || tag === normalizarTagParceiro(empresa.tagParceiro ?? "") || disponibilidade.status !== "disponivel"}
              data-testid={`button-save-tag-${empresa.id}`}
            >
              {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvar TAG
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-xs text-muted-foreground">TAG do Parceiro</p>
            <p className="font-mono font-semibold text-primary" data-testid={`text-tag-parceiro-${empresa.id}`}>
              {empresa.tagParceiro || "Ainda sem TAG"}
            </p>
          </div>
          {podeEditar ? (
            <Button type="button" size="sm" variant="outline" onClick={() => setEditando(true)}>Editar TAG</Button>
          ) : (
            <p className="text-xs text-muted-foreground" data-testid={`tag-parceiro-bloqueada-${empresa.id}`}>
              TAG não pode ser alterada neste momento.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function CreditosParceiroResumo({ resumo }: { resumo: ResumoCreditosParceiro }) {
  const itens = [
    { label: "Disponíveis", valor: resumo.disponivel, cor: "text-emerald-700" },
    { label: "Reservados", valor: resumo.reservado, cor: "text-amber-700" },
    { label: "Debitados", valor: resumo.debitado, cor: "text-slate-700" },
  ];
  return (
    <div className="mt-2 pt-2 border-t" data-testid={`creditos-parceiro-${resumo.parceiroId}`}>
      <p className="text-xs text-muted-foreground mb-1.5">Créditos</p>
      <div className="grid grid-cols-3 gap-2 text-center">
        {itens.map((i) => (
          <div key={i.label} className="rounded-md bg-slate-50 py-1.5">
            <p className={`text-base font-semibold tabular-nums ${i.cor}`}>{i.valor}</p>
            <p className="text-[11px] text-slate-500">{i.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Empresas() {
  const { data: empresas, isLoading } = useEmpresas();
  const { profile, session } = useAuth();
  const { modulos } = useModulos();
  const isOwner = profile?.roles?.includes("owner");
  const isVistoriadora = profile?.roles?.some((r: string) =>
    ["admin", "rt"].includes(r),
  );

  const { data: resumoCreditos } = useResumoCreditosParceiros(
    !!(isOwner || isVistoriadora) && modulos.creditosPrePagos,
  );
  const creditosPorParceiro = useMemo(
    () => new Map((resumoCreditos ?? []).map((r) => [r.parceiroId, r])),
    [resumoCreditos],
  );

  const [showFormVistoriadora, setShowFormVistoriadora] = useState(false);
  const [showFormParceiro, setShowFormParceiro] = useState(false);
  const [showImportarParceiros, setShowImportarParceiros] = useState(false);
  const [filterClienteId, setFilterClienteId] = useState<string>("all");
  const [searchVistoriadora, setSearchVistoriadora] = useState("");
  const [searchParceiro, setSearchParceiro] = useState("");
  const [filterUF, setFilterUF] = useState("all");

  const { toast } = useToast();

  const TIPO_LABELS: Record<string, string> = {
    auto_leve: "Auto/Leve",
    moto: "Moto",
    caminhao: "Caminhão",
    onibus: "Ônibus",
  };
  const TIPO_KEYS = ["auto_leve", "moto", "caminhao", "onibus"] as const;

  const modoTesteMutation = useMutation({
    mutationFn: async ({
      id,
      field,
      value,
    }: {
      id: string;
      field: "modoTeste" | "modoTesteNovosCadastros" | "modoTesteCreditoMetodo";
      value: boolean | string;
    }) => {
      const res = await fetch(`/api/empresas/${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ [field]: value }),
      });
      if (!res.ok) throw new Error("Erro ao salvar modo de teste");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.empresas.list.path] });
      toast({ title: "Modo de teste atualizado" });
    },
    onError: () => {
      toast({ title: "Erro ao salvar", variant: "destructive" });
    },
  });

  const vistoriadoras =
    empresas?.filter((e) => e.tipo === "VISTORIADORA") || [];
  const parceiros =
    empresas?.filter((e) => e.tipo === "PARCEIRO_DE_NEGOCIOS") || [];
  const minhaEmpresa = empresas?.find((e) => e.id === profile?.empresaId);

  const vistoriadorasFiltradas = useMemo(() => {
    let list =
      filterClienteId === "all"
        ? vistoriadoras
        : vistoriadoras.filter((v) => {
            const clienteSelecionado = parceiros.find(
              (p) => p.id === filterClienteId,
            );
            return clienteSelecionado?.vistoriadoraId === v.id;
          });
    if (searchVistoriadora.trim()) {
      const q = searchVistoriadora.toLowerCase();
      list = list.filter(
        (v) =>
          v.razaoSocial?.toLowerCase().includes(q) ||
          v.nomeFantasia?.toLowerCase().includes(q) ||
          v.tagEmpresa?.toLowerCase().includes(q) ||
          v.cidade?.toLowerCase().includes(q),
      );
    }
    return list;
  }, [filterClienteId, vistoriadoras, parceiros, searchVistoriadora]);

  const parceirosFiltrados = useMemo(() => {
    let list = [...parceiros].sort((a, b) =>
      b.criadoEm > a.criadoEm ? 1 : -1,
    );
    if (searchParceiro.trim()) {
      const q = searchParceiro.toLowerCase();
      list = list.filter(
        (p) =>
          p.razaoSocial?.toLowerCase().includes(q) ||
          p.nomeFantasia?.toLowerCase().includes(q) ||
          (p as EmpresaComTagParceiro).tagParceiro?.toLowerCase().includes(q) ||
          p.cidade?.toLowerCase().includes(q) ||
          p.cnpj?.includes(q) ||
          p.cpf?.includes(q),
      );
    }
    if (filterUF !== "all") {
      list = list.filter((p) => p.uf === filterUF);
    }
    return list;
  }, [parceiros, searchParceiro, filterUF]);

  const pageTitle = isOwner ? "Empresas" : isVistoriadora ? "Parceiros de Negócios" : "Minha empresa";
  const pageSubtitle = isOwner
    ? "Gerencie empresas vistoriadoras e parceiros de negócios"
    : "Gerencie os parceiros vinculados à sua empresa";

  return (
    <Shell>
      <div className="mb-8">
        <h1
          className="text-3xl font-bold font-display text-slate-900"
          data-testid="text-page-title"
        >
          {pageTitle}
        </h1>
        <p className="text-muted-foreground mt-1">{pageSubtitle}</p>
      </div>

      {isOwner && minhaEmpresa && (
        <div className="mb-6 flex items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
          <div>
            <p className="text-sm font-semibold text-amber-800">
              Novos cadastros entram em modo de teste
            </p>
            <p className="text-xs text-amber-700">
              Toda nova Vistoriadora ou Parceiro criado a partir de agora já
              nasce com o modo de teste ativado.
            </p>
          </div>
          <Switch
            checked={!!(minhaEmpresa as { modoTesteNovosCadastros?: boolean }).modoTesteNovosCadastros}
            onCheckedChange={(checked) =>
              modoTesteMutation.mutate({
                id: minhaEmpresa.id,
                field: "modoTesteNovosCadastros",
                value: checked,
              })
            }
          />
        </div>
      )}

      {isOwner && (
        <div className="mb-6">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <h2 className="text-lg font-semibold text-slate-800 mr-auto">
              Vistoriadoras ({vistoriadorasFiltradas.length}
              {filterClienteId !== "all" ? ` de ${vistoriadoras.length}` : ""})
            </h2>
            {parceiros.length > 0 && (
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-slate-400" />
                <Select
                  value={filterClienteId}
                  onValueChange={setFilterClienteId}
                >
                  <SelectTrigger
                    className="w-[220px]"
                    data-testid="select-filter-cliente"
                  >
                    <SelectValue placeholder="Todos os clientes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os clientes</SelectItem>
                    {parceiros.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.nomeFantasia || p.razaoSocial}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {filterClienteId !== "all" && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setFilterClienteId("all")}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            )}
            {!showFormVistoriadora && (
              <Button
                size="sm"
                onClick={() => setShowFormVistoriadora(true)}
                data-testid="button-add-vistoriadora"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nova Vistoriadora
              </Button>
            )}
          </div>

          {showFormVistoriadora && (
            <div className="mb-4">
              <CreateEmpresaForm
                tipo="VISTORIADORA"
                vistoriadoras={[]}
                onClose={() => setShowFormVistoriadora(false)}
              />
            </div>
          )}

          <div className="flex items-center gap-2 mb-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar vistoriadora..."
                value={searchVistoriadora}
                onChange={(e) => setSearchVistoriadora(e.target.value)}
                className="pl-8 h-9"
              />
            </div>
            {searchVistoriadora && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSearchVistoriadora("")}
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {isLoading ? (
              Array(3)
                .fill(0)
                .map((_, i) => <Skeleton key={i} className="h-48 rounded-xl" />)
            ) : vistoriadorasFiltradas.length > 0 ? (
              vistoriadorasFiltradas.map((empresa) => (
                <Card
                  key={empresa.id}
                  className="border-none shadow-md hover:shadow-lg transition-shadow"
                  data-testid={`card-empresa-${empresa.id}`}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <LogoAvatar
                          empresaId={empresa.id}
                          logoUrl={empresa.logoUrl}
                          fallback={
                            empresa.razaoSocial
                              ?.substring(0, 2)
                              .toUpperCase() || "E"
                          }
                          size="sm"
                        />
                        <div>
                          <CardTitle className="text-base">
                            {empresa.razaoSocial}
                          </CardTitle>
                          {empresa.nomeFantasia && (
                            <p className="text-xs text-muted-foreground">
                              {empresa.nomeFantasia}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1.5">
                        <Badge
                          variant="secondary"
                          className="bg-blue-100 text-blue-700"
                        >
                          Vistoriadora
                        </Badge>
                        <div className="flex items-center gap-1.5">
                          {(empresa as { modoTeste?: boolean }).modoTeste && (
                            <Badge className="bg-amber-100 text-amber-700 text-[10px]">
                              TESTE
                            </Badge>
                          )}
                          <Switch
                            checked={!!(empresa as { modoTeste?: boolean }).modoTeste}
                            onCheckedChange={(checked) =>
                              modoTesteMutation.mutate({
                                id: empresa.id,
                                field: "modoTeste",
                                value: checked,
                              })
                            }
                          />
                        </div>
                        {(empresa as { modoTeste?: boolean }).modoTeste && (
                          <Select
                            value={
                              (empresa as { modoTesteCreditoMetodo?: string })
                                .modoTesteCreditoMetodo || "sandbox_asaas"
                            }
                            onValueChange={(value) =>
                              modoTesteMutation.mutate({
                                id: empresa.id,
                                field: "modoTesteCreditoMetodo",
                                value,
                              })
                            }
                          >
                            <SelectTrigger className="h-6 text-[10px] w-[150px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="sandbox_asaas">
                                Sandbox Asaas
                              </SelectItem>
                              <SelectItem value="simulacao">
                                Simulação de Compra
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    {empresa.cnpj && (
                      <p className="text-slate-600">
                        CNPJ: {formatCnpj(empresa.cnpj)}
                      </p>
                    )}
                    {empresa.tagEmpresa && (
                      <div className="mt-2 pt-2 border-t">
                        <p className="text-xs text-muted-foreground mb-1">
                          Tag para Laudos
                        </p>
                        <p className="font-mono font-bold text-primary">
                          {empresa.tagEmpresa}
                        </p>
                      </div>
                    )}
                    {empresa.email && (
                      <div className="flex items-center gap-2 text-slate-500">
                        <Mail className="w-3.5 h-3.5" />
                        <span className="truncate">{empresa.email}</span>
                      </div>
                    )}
                    {empresa.telefone && (
                      <div className="flex items-center gap-2 text-slate-500">
                        <Phone className="w-3.5 h-3.5" />
                        <span>{empresa.telefone}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-slate-500">
                      <MapPin className="w-3.5 h-3.5" />
                      <span>
                        {empresa.cidade ? `${empresa.cidade}, ` : ""}
                        {empresa.uf}
                      </span>
                    </div>
                    {empresa.codigoConvite && (
                      <div className="mt-2 pt-2 border-t">
                        <p className="text-xs text-muted-foreground">
                          Código de Convite
                        </p>
                        <p className="font-mono font-bold text-primary">
                          {empresa.codigoConvite}
                        </p>
                      </div>
                    )}
                    <div className="mt-2 pt-2 border-t">
                      <p className="text-xs text-muted-foreground flex items-center gap-1 mb-2">
                        <DollarSign className="w-3 h-3" />
                        Valor por Laudo (por tipo)
                      </p>
                      <div className="space-y-0.5">
                        {TIPO_KEYS.map((tipo) => {
                          const v = (
                            empresa.valoresPorTipo as Record<string, string>
                          )?.[tipo];
                          return (
                            <div
                              key={tipo}
                              className="flex items-center justify-between text-xs"
                            >
                              <span className="text-slate-500">
                                {TIPO_LABELS[tipo]}
                              </span>
                              <span className="font-medium text-slate-700">
                                {v ? (
                                  Number(v).toLocaleString("pt-BR", {
                                    style: "currency",
                                    currency: "BRL",
                                  })
                                ) : (
                                  <span className="text-slate-400">—</span>
                                )}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <div className="col-span-full text-center py-12 text-muted-foreground">
                {filterClienteId !== "all"
                  ? "Nenhuma vistoriadora encontrada para este cliente"
                  : "Nenhuma empresa vistoriadora encontrada"}
              </div>
            )}
          </div>
        </div>
      )}

      {(isOwner || isVistoriadora) && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-800">
              {isOwner
                ? `Parceiros de Negócios (${parceiros.length})`
                : `Meus Parceiros (${parceiros.length})`}
            </h2>
            <div className="flex items-center gap-2">
              {isVistoriadora && !isOwner && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowImportarParceiros(true)}
                  data-testid="button-import-parceiros"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Importar CSV/XLSX
                </Button>
              )}
              {(isOwner || isVistoriadora) && !showFormParceiro && (
                <Button
                  size="sm"
                  onClick={() => setShowFormParceiro(true)}
                  data-testid="button-add-parceiro"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Novo Parceiro
                </Button>
              )}
            </div>
          </div>

          {showFormParceiro && (
            <div className="mb-4">
              <CreateEmpresaForm
                tipo="PARCEIRO_DE_NEGOCIOS"
                vistoriadoras={
                  isOwner
                    ? vistoriadoras.map((c) => ({
                        id: c.id,
                        razaoSocial: c.razaoSocial,
                      }))
                    : []
                }
                isOwnerCreating={!!isOwner}
                artMultipla={!isOwner && modulos.artMultipla}
                artPorRt={!isOwner && modulos.artPorRt}
                onClose={() => setShowFormParceiro(false)}
              />
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 mb-4">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar parceiro..."
                value={searchParceiro}
                onChange={(e) => setSearchParceiro(e.target.value)}
                className="pl-8 h-9"
              />
            </div>
            <Select value={filterUF} onValueChange={setFilterUF}>
              <SelectTrigger className="w-[100px] h-9">
                <SelectValue placeholder="UF" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {UF_LIST.map((uf) => (
                  <SelectItem key={uf} value={uf}>
                    {uf}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(searchParceiro || filterUF !== "all") && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchParceiro("");
                  setFilterUF("all");
                }}
              >
                <X className="w-4 h-4 mr-1" />
                Limpar
              </Button>
            )}
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {isLoading ? (
              Array(3)
                .fill(0)
                .map((_, i) => <Skeleton key={i} className="h-40 rounded-xl" />)
            ) : parceirosFiltrados.length > 0 ? (
              parceirosFiltrados.map((empresa) => (
                  <Card
                    key={empresa.id}
                    className="border-none shadow-md hover:shadow-lg transition-shadow"
                    data-testid={`card-empresa-${empresa.id}`}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <LogoAvatar
                            empresaId={empresa.id}
                            logoUrl={empresa.logoUrl}
                            fallback={
                              empresa.razaoSocial
                                ?.substring(0, 2)
                                .toUpperCase() || "E"
                            }
                            size="sm"
                          />
                          <div>
                            <CardTitle className="text-base">
                              {empresa.razaoSocial}
                              {(empresa as any).idProprio != null && (
                                <span className="ml-1.5 text-sm font-normal text-muted-foreground">
                                  (#{(empresa as any).idProprio})
                                </span>
                              )}
                            </CardTitle>
                            {empresa.nomeFantasia && (
                              <p className="text-xs text-muted-foreground">
                                {empresa.nomeFantasia}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1.5">
                          <Badge
                            variant="secondary"
                            className="bg-orange-100 text-orange-700"
                          >
                            Parceiro
                          </Badge>
                          <div className="flex items-center gap-1.5">
                            {(empresa as { modoTeste?: boolean }).modoTeste && (
                              <Badge className="bg-amber-100 text-amber-700 text-[10px]">
                                TESTE
                              </Badge>
                            )}
                            {isOwner && (
                              <Switch
                                checked={!!(empresa as { modoTeste?: boolean }).modoTeste}
                                onCheckedChange={(checked) =>
                                  modoTesteMutation.mutate({
                                    id: empresa.id,
                                    field: "modoTeste",
                                    value: checked,
                                  })
                                }
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      {(empresa.cnpj || empresa.cpf) && (
                        <p className="text-slate-600">
                          {empresa.cnpj
                            ? `CNPJ: ${formatCnpj(empresa.cnpj)}`
                            : `CPF: ${formatCpf(empresa.cpf ?? "")}`}
                        </p>
                      )}
                      <TagParceiroResumo empresa={empresa as EmpresaComTagParceiro} token={session?.access_token} />
                      {empresa.email && (
                        <div className="flex items-center gap-2 text-slate-500">
                          <Mail className="w-3.5 h-3.5" />
                          <span className="truncate">{empresa.email}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-slate-500">
                        <MapPin className="w-3.5 h-3.5" />
                        <span>
                          {empresa.cidade ? `${empresa.cidade}, ` : ""}
                          {empresa.uf}
                        </span>
                      </div>

                      <div className="flex items-center gap-2 text-slate-500">
                        <CalendarFold className="w-3.5 h-3.5" />
                        <span>
                          {new Date(empresa.criadoEm).toLocaleDateString(
                            "pt-BR",
                          )}
                        </span>
                      </div>
                      {modulos.creditosPrePagos && resumoCreditos && (
                        <CreditosParceiroResumo
                          resumo={
                            creditosPorParceiro.get(empresa.id) ?? {
                              parceiroId: empresa.id,
                              disponivel: 0,
                              reservado: 0,
                              debitado: 0,
                            }
                          }
                        />
                      )}
                    </CardContent>
                  </Card>
                ))
            ) : (
              <div className="col-span-full text-center py-12 text-muted-foreground">
                Nenhum parceiro de negócios encontrado
              </div>
            )}
          </div>
        </div>
      )}

      <ImportarParceirosDialog
        open={showImportarParceiros}
        onClose={() => setShowImportarParceiros(false)}
      />

      {!isOwner && !isVistoriadora && minhaEmpresa && (
        <div>
          <h2 className="text-lg font-semibold text-slate-800 mb-4">
            Minha Empresa
          </h2>
          <Card
            className="border-none shadow-md max-w-md"
            data-testid={`card-empresa-${minhaEmpresa.id}`}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <LogoAvatar
                  empresaId={minhaEmpresa.id}
                  logoUrl={minhaEmpresa.logoUrl}
                  fallback={
                    minhaEmpresa.razaoSocial?.substring(0, 2).toUpperCase() ||
                    "E"
                  }
                  size="sm"
                />
                <div>
                  <CardTitle className="text-base">
                    {minhaEmpresa.razaoSocial}
                  </CardTitle>
                  {minhaEmpresa.nomeFantasia && (
                    <p className="text-xs text-muted-foreground">
                      {minhaEmpresa.nomeFantasia}
                    </p>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {minhaEmpresa.tipo === "PARCEIRO_DE_NEGOCIOS" && (
                <TagParceiroResumo empresa={minhaEmpresa as EmpresaComTagParceiro} token={session?.access_token} />
              )}
              {minhaEmpresa.email && (
                <div className="flex items-center gap-2 text-slate-500">
                  <Mail className="w-3.5 h-3.5" />
                  <span className="truncate">{minhaEmpresa.email}</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-slate-500">
                <MapPin className="w-3.5 h-3.5" />
                <span>
                  {minhaEmpresa.cidade ? `${minhaEmpresa.cidade}, ` : ""}
                  {minhaEmpresa.uf}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </Shell>
  );
}
